import {Button, Message} from "@arco-design/web-react";
import { GoogleOutlined } from "@ant-design/icons";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { endpoints } from "../api/endpoints";
import { resetCachedToken } from "../setupAxios";
import axios from "axios";
import { routes } from "../routes/routes";
import { clearUser, setUser } from "../store/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import { defaultAuthenticatedPath } from "../utils/defaultAuthenticatedPath";
import { isAuthPopupClosedByUser } from "../utils/isAuthPopupClosedByUser";
import { FirebaseError } from "firebase/app";
import { useTranslation } from "react-i18next";
import { getOAuthButtonProps } from "../utils/oauthButtonStyle";
import { completeAuthRedirect } from "../utils/authReturnUrl";

type GoogleAuthButtonProps = {
    variant?: "default" | "icon";
};

export const GoogleAuthButton = ({ variant = "default" }: GoogleAuthButtonProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const eventIntakeEnabled = useSelector(
        (state: RootState) => state.config.configLoaded && state.config.eventIntakeEnabled,
    );

    const handleGoogleLogin = async () => {
        const auth = getAuth();
        const provider = new GoogleAuthProvider();

        try {
            resetCachedToken();
            dispatch(clearUser());
            const result = await signInWithPopup(auth, provider);

            if (result) {
                const firebaseUser = result.user;
                const idToken = await firebaseUser.getIdToken();
                const response = await axios.get(endpoints.whoami, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });

                dispatch(setUser({ ...response.data }));
                completeAuthRedirect(
                    defaultAuthenticatedPath(
                        response.data,
                        eventIntakeEnabled,
                        new URLSearchParams(window.location.search),
                    ),
                );
            }
        } catch (error) {
            console.error("Google login failed:", error);

            if (isAuthPopupClosedByUser(error)) {
                return;
            } 

            if (error instanceof FirebaseError && error.code === "auth/account-exists-with-different-credential") {
                Message.error(t('notification_messages.email_exists_with_outlook_error')
                );
                return;
            }

            Message.error(t('notification_messages.email_not_found_error'));
        }
    };

    if (variant === "icon") {
        return (
            <Button
                className="auth-sso-btn"
                onClick={handleGoogleLogin}
                icon={<GoogleOutlined />}
                aria-label={t('login_page.google_login')}
            />
        );
    }

    return (
        <Button
            onClick={handleGoogleLogin}
            shape="round"
            size="large"
            icon={<GoogleOutlined />}
            {...getOAuthButtonProps({ marginTop: 10 })}
        >
            {t('login_page.google_login')}
        </Button>
    );
};
